
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Check, Database, Lock, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ZohoCRMIntegrationSettingsProps {
  initialConnected?: boolean;
  onSaved: () => Promise<void>;
}

const ZohoCRMIntegrationSettings: React.FC<ZohoCRMIntegrationSettingsProps> = ({ 
  initialConnected = false,
  onSaved 
}) => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(initialConnected);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [domain, setDomain] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleConnect = async () => {
    try {
      setIsLoading(true);
      // Implement Zoho CRM connection logic
      // This would typically be an API call to your backend
      
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      await onSaved();
      setIsConnected(true);
      
      toast({
        title: "Connection successful",
        description: "Your Zoho CRM account has been connected.",
      });
    } catch (error) {
      toast({
        title: "Connection failed",
        description: "Failed to connect to Zoho CRM. Please check your credentials.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Database className="mr-2 h-6 w-6" />
          Zoho CRM Integration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Zoho CRM Connection</Label>
            <Switch 
              checked={isConnected}
              onCheckedChange={setIsConnected}
            />
          </div>

          {isConnected ? (
            <div className="space-y-4">
              <div>
                <Label>Zoho CRM Domain</Label>
                <Input 
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="yourcompany.zohocrm.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  The domain for your Zoho CRM account
                </p>
              </div>
              <div>
                <Label>Client ID</Label>
                <Input 
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Enter Zoho Client ID"
                />
              </div>
              <div>
                <Label>Client Secret</Label>
                <div className="flex items-center space-x-2">
                  <Input 
                    type="password"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    placeholder="Enter Zoho Client Secret"
                  />
                  <Button variant="outline" size="icon">
                    <Lock className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  For security, we mask your client secret. You can generate a new secret in your Zoho Developer Console.
                </p>
              </div>
              
              <div className="pt-2">
                <Button 
                  onClick={handleConnect}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Connecting..." : "Update Connection"}
                </Button>
              </div>
              
              <div className="bg-muted p-3 rounded-md">
                <h4 className="text-sm font-medium mb-2">Connection Status</h4>
                <div className="flex items-center">
                  <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                  <span className="text-sm">Connected to Zoho CRM</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Last synced: Just now</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect your Zoho CRM account to sync contacts and manage leads directly from your dashboard.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">1</span>
                  <span>Create an application in the <a href="https://api-console.zoho.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Zoho Developer Console</a></span>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">2</span>
                  <span>Add the following redirect URI: <code className="bg-muted px-1 py-0.5 rounded text-xs">{window.location.origin}/zoho-callback</code></span>
                </li>
                <li className="flex items-start">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">3</span>
                  <span>Copy the Client ID and Client Secret from your Zoho application</span>
                </li>
              </ul>
              <Button onClick={handleConnect} className="w-full">
                Connect to Zoho CRM
              </Button>
            </div>
          )}
          
          {/* Status Button */}
          <div className="pt-4 w-full flex justify-end">
            <Button 
              variant={isConnected ? "success" : "outline"}
              className={`${isConnected ? "bg-green-500 hover:bg-green-600" : "bg-gray-200 text-gray-700"} flex items-center gap-2`}
              size="sm"
              disabled={true} // This is just a status indicator
            >
              {isConnected ? (
                <>
                  <Check className="h-4 w-4" />
                  <span>Live</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  <span>Not Connected</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ZohoCRMIntegrationSettings;
